import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import UserNotification from "util/UserNotification";
import { defaultCompare } from "logic/DefaultCompare";
import { defaultOnError } from "util/conditional/onError";
import {
  bulkSigmaRule,
  bulkAddNotifications,
  bulkSigmaRepoDelete,
  fetchSigmaRules,
  fetchStreams,
  fetchNotifications,
  createSigmaRule,
  updateSigmaRule,
  toggleSigmaRule,
  deleteSigmaRule,
  listGitRepos,
  addGitRepo,
  updateGitRepo,
  deleteSigmaRepo,
  listRepoContent,
  importRulesFromRepo,
  validateSigmaRule,
  addDefaultRepo,
  importAllRulesFromRepo,
  refreshRepoRules,
  uploadSigmaRules,
  validateSigmaRuleZip,
  getSigmaRule,
  fetchSigmaRulesFilterOptions
} from "./api/sigmaAPI";
export const sigmaRuleKey = "get-sigma-rule";
const sigmaRulesKey = "get-sigma-rules";
export function useGetSigmaRules({
  page,
  perPage,
  query = void 0,
  orderBy = void 0,
  direction = void 0,
  filters = void 0
}, userTimezone, onlyMatchingSystemConfig) {
  const { data, isLoading } = useQuery({
    queryKey: [
      sigmaRulesKey,
      page,
      perPage,
      query,
      orderBy,
      direction,
      filters,
      userTimezone,
      onlyMatchingSystemConfig
    ],
    queryFn: () => defaultOnError(
      fetchSigmaRules(page, perPage, query, orderBy, direction, filters, userTimezone, onlyMatchingSystemConfig),
      "Error fetching Sigma rules"
    ),
    retry: 2,
    placeholderData: keepPreviousData
  });
  return {
    loadingRules: isLoading,
    rules: isLoading ? [] : data == null ? void 0 : data.sigma_rules,
    pagination: {
      page: (data == null ? void 0 : data.page) || page,
      perPage: (data == null ? void 0 : data.per_page) || perPage,
      total: (data == null ? void 0 : data.total) || 0,
      grandTotal: (data == null ? void 0 : data.grand_total) || 0,
      count: (data == null ? void 0 : data.count) || 0
    }
  };
}
export function useFetchSigmaRulesFilterOptions() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: fetchSigmaRulesFilterOptions,
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    fetchSigmaRulesFilterOptions: mutateAsync,
    fetchingSigmaRulesFilterOptions: isLoading
  };
}
export function useGetSigmaRule(ruleId) {
  const { data, isLoading } = useQuery({
    queryKey: [sigmaRuleKey, ruleId],
    queryFn: () => defaultOnError(getSigmaRule(ruleId), "Error fetching Sigma Rule"),
    retry: false,
    enabled: !!ruleId
  });
  return {
    loadingRule: isLoading,
    rule: data
  };
}
export function useGetStreams(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["streams-index"],
    queryFn: () => defaultOnError(fetchStreams(), "Error fetching streams"),
    retry: 1,
    enabled: execute
  });
  return {
    loadingStreams: isLoading && !!execute,
    streams: (data == null ? void 0 : data.streams.sort((a, b) => {
      if (a.title > b.title) return 1;
      if (a.title < b.title) return -1;
      return 0;
    })) || [],
    streamCategories: (data == null ? void 0 : data.streams.reduce((acc, stream) => {
      var _a;
      (_a = stream.categories) == null ? void 0 : _a.forEach((category) => {
        if (!acc.includes(category)) acc.push(category);
      });
      return acc;
    }, []).sort((a, b) => defaultCompare(a, b))) || []
  };
}
export function useGetNotifications(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["notifications-index"],
    queryFn: () => defaultOnError(fetchNotifications(), "Error fetching notifications"),
    retry: 1,
    enabled: execute
  });
  return {
    loadingNotifications: isLoading && !!execute,
    notifications: (data == null ? void 0 : data.notifications.sort((a, b) => {
      if (a.title > b.title) return 1;
      if (a.title < b.title) return -1;
      return 0;
    })) || []
  };
}
export function useValidateSigmaRule() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: validateSigmaRule,
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: ["validate-sigma-rule"]
    }),
    onError: (error) => UserNotification.error(error.message)
  });
  return { validateSigmaRule: mutateAsync, isValidating: isLoading };
}
export function useCreateSigmaRule() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: createSigmaRule,
    onSuccess: () => {
      UserNotification.success("Rule created");
      queryClient.invalidateQueries({
        queryKey: [sigmaRulesKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { createSigmaRule: mutateAsync };
}
export function useUpdateSigmaRule() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: updateSigmaRule,
    onSuccess: () => {
      UserNotification.success("Rule updated");
      queryClient.invalidateQueries({
        queryKey: [sigmaRulesKey]
      });
      queryClient.invalidateQueries({
        queryKey: [sigmaRuleKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { updateSigmaRule: mutateAsync };
}
export function useToggleSigmaRule() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: toggleSigmaRule,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [sigmaRulesKey]
      });
      queryClient.invalidateQueries({
        queryKey: ["threat-coverage"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { toggleSigmaRule: mutateAsync };
}
export function useDeleteSigmaRule() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: deleteSigmaRule,
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: [sigmaRulesKey]
    }),
    onError: (error) => UserNotification.error(error.message)
  });
  return { deleteSigmaRule: mutateAsync };
}
export function useListGitRepos({ page, perPage, query = void 0, orderBy = void 0, direction = void 0 }, enabled = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["list-git-repos", page, perPage, query, orderBy, direction],
    queryFn: () => defaultOnError(listGitRepos(page, perPage, query, orderBy, direction), "Error fetching Git repositories"),
    retry: 1,
    enabled,
    placeholderData: keepPreviousData
  });
  return {
    loadingRepos: isLoading,
    repos: isLoading ? [] : data == null ? void 0 : data.sigma_git_repositories,
    pagination: {
      page: (data == null ? void 0 : data.page) || page,
      perPage: (data == null ? void 0 : data.per_page) || perPage,
      total: (data == null ? void 0 : data.total) || 0,
      grandTotal: (data == null ? void 0 : data.grand_total) || 0,
      count: (data == null ? void 0 : data.count) || 0
    }
  };
}
export function useListRepoContent(repoId, path, query) {
  const { data, isLoading } = useQuery({
    queryKey: ["list-git-repo-content", repoId, path, query],
    queryFn: () => defaultOnError(listRepoContent(repoId, path, query), "Error listing repository"),
    retry: 1,
    enabled: !!repoId
  });
  return {
    loadingRepoRules: isLoading && !!repoId,
    repoRules: data ? data.sort((a, b) => {
      if (a.type > b.type) return 1;
      if (a.type < b.type) return -1;
      return 0;
    }) : null
  };
}
export function useAddGitRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addGitRepo,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const error = data.success_count > 0 ? {
          type: "warning",
          message: `Repo added and imported ${data.success_count} rules successfully, ${data.failure_count} rules failed to import.`
        } : { type: "error", message: `Failed to add repo: ${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(`Repo added and all ${data.success_count} rules imported`);
      }
      queryClient.invalidateQueries({
        queryKey: ["list-git-repos"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { addGitRepo: mutateAsync, addingRepo: isLoading };
}
export function useAddDefaultGitRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addDefaultRepo,
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: ["list-git-repos"]
    }),
    onError: (error) => UserNotification.error(error.message)
  });
  return { addDefaultRepo: mutateAsync, addingDefaultRepo: isLoading };
}
export function useUpdateGitRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateGitRepo,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const error = data.success_count > 0 ? {
          type: "warning",
          message: `Repo updated: updated ${data.success_count} rules successfully, ${data.failure_count} rules failed to update.`
        } : { type: "error", message: `Failed to update repo: ${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(`Repo updated with all ${data.success_count} rules.`);
      }
      queryClient.invalidateQueries({
        queryKey: ["list-git-repos"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { updateGitRepo: mutateAsync, updatingRepo: isLoading };
}
export function useImportRulesFromRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: importRulesFromRepo,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const error = data.success_count > 0 ? { type: "warning", message: "Rules imported with errors" } : { type: "error", message: "All rules failed to import" };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(`All ${data.success_count} rules imported`);
      }
      queryClient.invalidateQueries({
        queryKey: [sigmaRulesKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    importRulesFromRepo: mutateAsync,
    importingRules: isLoading
  };
}
export function useImportAllRulesFromRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: importAllRulesFromRepo,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const warnMsg = `${data.success_count} rule${data.success_count === 1 ? "" : "s"} imported successfully. ${data.failure_count} rule${data.failure_count === 1 ? "" : "s"} failed to import. See server log for more detailed information.`;
        const error = data.success_count > 0 ? { type: "warning", message: warnMsg } : { type: "error", message: "All rules failed to import" };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(`All ${data.success_count} rules imported`);
      }
      queryClient.invalidateQueries({
        queryKey: [sigmaRulesKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    importRulesFromRepo: mutateAsync,
    importingRules: isLoading
  };
}
export function useRefreshRepoRules() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: refreshRepoRules,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const warnMsg = `${data.success_count} rule${data.success_count === 1 ? "" : "s"} refreshed successfully. ${data.failure_count} rule${data.failure_count === 1 ? "" : "s"} failed to refresh. See server log for more detailed information.`;
        const error = data.success_count > 0 ? { type: "warning", message: warnMsg } : { type: "error", message: "All rules failed to refresh" };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(`All ${data.success_count} rules refreshed`);
      }
      queryClient.invalidateQueries({
        queryKey: [sigmaRulesKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    refreshRules: mutateAsync,
    refreshingRules: isLoading
  };
}
export function useDeleteSigmaRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteSigmaRepo,
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: ["list-git-repos"]
    }),
    onError: (error) => UserNotification.error(error.message)
  });
  return { deleteSigmaRepo: mutateAsync, deletingRepo: isLoading };
}
export function useBulkSigmaOperations() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkSigmaRule,
    onSuccess: () => {
      UserNotification.success("Action completed. ");
      queryClient.invalidateQueries({
        queryKey: [sigmaRulesKey]
      });
      queryClient.invalidateQueries({
        queryKey: ["threat-coverage"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { bulkSigmaOperations: mutateAsync, isMutating: isLoading };
}
export function useBulkAddNotifications() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkAddNotifications,
    onSuccess: (data) => {
      UserNotification.success(
        `Added notification for ${data.success_count} rules. (${data.failure_count} rules failed to update.)`
      );
      queryClient.invalidateQueries({
        queryKey: [sigmaRulesKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { bulkAddNotifications: mutateAsync, isAdding: isLoading };
}
export function useBulkDeleteSigmaRepos() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkSigmaRepoDelete,
    onSuccess: () => {
      UserNotification.success("Action completed");
      queryClient.invalidateQueries({
        queryKey: ["list-git-repos"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { bulkDeleteSigmaRepos: mutateAsync, isMutating: isLoading };
}
export function useUploadSigmaRules() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: uploadSigmaRules,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const error = data.success_count > 0 ? {
          type: "warning",
          message: `${data.success_count} rules successfully imported, ${data.failure_count} rules failed to import.`
        } : { type: "error", message: `Failed to import all rules: ${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(`All ${data.success_count} rules imported successfully`);
      }
      queryClient.invalidateQueries({
        queryKey: [sigmaRulesKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { uploadSigmaRules: mutateAsync, uploadingRules: isLoading };
}
export function useValidateSigmaRuleZip() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: validateSigmaRuleZip,
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    validateSigmaRuleZip: mutateAsync,
    isValidating: isLoading
  };
}
